import { databases, databaseId, appwriteConfig } from '../config/appwrite';
import { Dossier } from '../types';
import { ID, Query } from 'appwrite';

export class DossierService {
  // Alle Dossiers abrufen
  static async getAllDossiers(): Promise<Dossier[]> {
    try {
      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.dossiers,
        [Query.orderAsc('title'), Query.limit(100)] // Appwrite verwendet 'title'
      );

      return response.documents.map((doc: any) => ({
        id: doc.$id,
        title: doc.title,
        platformId: doc.platformId,
        description: doc.description,
        isActive: true, // Standardwert
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      }));
    } catch (error) {
      throw new Error('Dossiers konnten nicht abgerufen werden');
    }
  }

  // Dossier nach ID abrufen
  static async getDossierById(id: string): Promise<Dossier> {
    try {
      const doc = await databases.getDocument(
        databaseId,
        appwriteConfig.collections.dossiers,
        id
      );

      return {
        id: doc.$id,
        title: doc.title,
        platformId: doc.platformId,
        description: doc.description,
        isActive: true, // Standardwert
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      };
    } catch (error) {
      throw new Error('Dossier konnte nicht abgerufen werden');
    }
  }

  // Neues Dossier erstellen
  static async createDossier(dossierData: Partial<Dossier>): Promise<Dossier> {
    try {
      const doc = await databases.createDocument(
        databaseId,
        appwriteConfig.collections.dossiers,
        ID.unique(),
        {
          title: dossierData.title,
          platformId: dossierData.platformId,
          description: dossierData.description,
          // isActive wird von Appwrite automatisch auf true gesetzt
        }
      );

      return {
        id: doc.$id,
        title: doc.title,
        platformId: doc.platformId,
        description: doc.description,
        isActive: true, // Standardwert
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      };
    } catch (error) {
      throw new Error('Dossier konnte nicht erstellt werden');
    }
  }

  // Dossier aktualisieren
  static async updateDossier(id: string, dossierData: Partial<Dossier>): Promise<Dossier> {
    try {
      const doc = await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.dossiers,
        id,
        {
          title: dossierData.title,
          platformId: dossierData.platformId,
          description: dossierData.description,
          // isActive wird von Appwrite automatisch verwaltet
        }
      );

      return {
        id: doc.$id,
        title: doc.title,
        platformId: doc.platformId,
        description: doc.description,
        isActive: true, // Standardwert
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      };
    } catch (error) {
      throw new Error('Dossier konnte nicht aktualisiert werden');
    }
  }

  // Dossier löschen
  static async deleteDossier(id: string): Promise<void> {
    try {
      await databases.deleteDocument(
        databaseId,
        appwriteConfig.collections.dossiers,
        id
      );
    } catch (error) {
      throw new Error('Dossier konnte nicht gelöscht werden');
    }
  }

  // Aktive Dossiers abrufen
  static async getActiveDossiers(): Promise<Dossier[]> {
    try {
      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.dossiers,
        [Query.orderAsc('title'), Query.limit(100)]
      );

      return response.documents.map((doc: any) => ({
        id: doc.$id,
        title: doc.title,
        platformId: doc.platformId,
        description: doc.description,
        isActive: true, // Standardwert
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      }));
    } catch (error) {
      throw new Error('Aktive Dossiers konnten nicht abgerufen werden');
    }
  }

  // Dossiers nach Plattform abrufen
  static async getDossiersByPlatform(platformId: string): Promise<Dossier[]> {
    try {
      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.dossiers,
        [
          Query.equal('platformId', platformId),
          Query.orderAsc('title'),
          Query.limit(100)
        ]
      );

      return response.documents.map((doc: any) => ({
        id: doc.$id,
        title: doc.title,
        platformId: doc.platformId,
        description: doc.description,
        isActive: true, // Standardwert
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      }));
    } catch (error) {
      throw new Error('Dossiers für diese Plattform konnten nicht abgerufen werden');
    }
  }
}
